/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Grid,
  Flex,
  Box,
  ArrowLink,
  Sticky,
  useStickyState,
} from '@tishman/components';
import React, {
  forwardRef,
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AddToCartNav } from '../AddToCartNav';

import { CheckoutCountdown } from './CheckoutCountdown';

import type { GridProps } from '@tishman/components';

const BuyTicketsBarColumn = ({
  children,
  sx,
  ...props
}: Omit<GridProps, 'ref'>) => (
  <Grid
    gap={2}
    sx={{
      flexShrink: 0,
      gridAutoFlow: 'column',
      gridAutoColumns: 'max-content',
      alignItems: 'center',
      transform: 'translate3d(0,0,0)',
      minHeight: ['87px', '80px'],
      ...sx,
    }}
    {...props}
  >
    {children}
  </Grid>
);

export interface BuyTicketsBarProps {
  children?: React.ReactNode;
  title: React.ReactNode;
  price: React.ReactNode;
}

const BuyTicketsBarContent = ({
  children,
  title,
  price,
  containerRef,
}: BuyTicketsBarProps & {
  containerRef?: React.RefObject<HTMLDivElement>;
}) => {
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const observerOptions = {
      root: null, // The viewport is the root
      rootMargin: '0px', // No margin
      threshold: 0.0, // Intersection ratio of 0.0 or greater triggers callback
    };

    const callback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      });
    };

    const observer = new IntersectionObserver(callback, observerOptions);

    if (containerRef && 'current' in containerRef && containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Cleanup: disconnect the observer when the component unmounts
    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  return (
    <Flex
      sx={{
        position: 'sticky',
        top: 0,
        maxWidth: isSticky ? 'inherit' : 'container',
        height: isSticky ? 'auto' : 0,
        overflow: 'hidden',
        mx: 'auto',
        px: isSticky ? [3, null, 4] : [0, null, 3],
        justifyContent: isSticky
          ? 'space-between'
          : ['stretch', null, 'center'],
        bg: isSticky ? 'background' : 'transparent',
        transform: 'translate3d(0,0,0)',
        zIndex: isSticky ? 'stickyOverlay' : 'initial',
        width: '100%',
      }}
    >
      <BuyTicketsBarColumn
        sx={{
          opacity: isSticky ? 1 : 0,
          display: ['none', null, 'inherit'],
        }}
      >
        <ArrowLink href="/buy-tickets/" label={'BACK'} reverse />
      </BuyTicketsBarColumn>
      <Flex
        sx={{
          flexDirection: [isSticky ? 'row' : 'column', null, 'row'],
          alignItems: 'center',
          justifyContent: 'center',
          columnGap: [2, 3],
          flex: 1,
          opacity: isSticky ? 1 : 0,
        }}
      >
        {title}
        <CheckoutCountdown showDescription={!isSticky} />
        {price}
      </Flex>
      <BuyTicketsBarColumn
        sx={{
          justifySelf: 'end',
          opacity: isSticky ? 1 : 0,
        }}
      >
        <Box sx={{ textAlign: 'right' }}>
          <AddToCartNav />
          <Flex sx={{ alignItems: 'center' }}>{children}</Flex>
        </Box>
      </BuyTicketsBarColumn>
    </Flex>
  );
};

export function BuyTicketsBar(
  props: Omit<BuyTicketsBarProps, 'intersectionRef'>,
): JSX.Element {
  const intersectionRef = useRef<HTMLDivElement>(null);

  return (
    <Fragment>
      <Box
        as="div"
        className="BuyTicketsBar-container"
        ref={intersectionRef}
        sx={{
          height: '1px',
        }}
      ></Box>
      <BuyTicketsBarContent {...props} containerRef={intersectionRef} />
    </Fragment>
  );
}
